import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Upload, Image, FileText, Link2, Trash2, ExternalLink, Copy, Loader2, Check, Download } from 'lucide-react';
import { logger } from '@/lib/logger';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface LandingPage {
  id: string;
  vanity_slug: string;
  landing_title: string | null;
  landing_description: string | null;
  file_name: string | null;
  file_path: string | null;
  file_size: number | null;
  content_type: string | null;
  og_image_path: string | null;
  landing_page_url: string | null;
  created_at: string;
  view_count: number;
}

// Helper to get full OG image URL
const getOgImageUrl = (ogPath: string | null): string | null => {
  if (!ogPath) return null;
  const cleanPath = ogPath.replace(/^file-uploads\//, '');
  return `${SUPABASE_URL}/storage/v1/object/public/file-uploads/${cleanPath}`;
};

const SUPABASE_URL = 'https://wjyaifrcyjmjxixsegij.supabase.co';

// Get base URL dynamically - prefer env var, fallback to window.location.origin
const getBaseUrl = (): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PUBLIC_SITE_URL) {
    return import.meta.env.VITE_PUBLIC_SITE_URL;
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'https://warp9-lead-score.lovable.app';
};

export const SimpleLandingCMS = () => {
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [uploadingFileFor, setUploadingFileFor] = useState<string | null>(null);
  const [uploadingImageFor, setUploadingImageFor] = useState<string | null>(null);
  const [regeneratingFor, setRegeneratingFor] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [downloadingThumbnailFor, setDownloadingThumbnailFor] = useState<string | null>(null);
  
  // Step 3: OG metadata editing
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingMetadata, setSavingMetadata] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [activePageId, setActivePageId] = useState<string | null>(null);

  useEffect(() => {
    fetchPages();
  }, []);


  const fetchPages = async () => {
    try {
      const { data, error } = await supabase
        .from('file_uploads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPages(data || []);
    } catch (error) {
      logger.error('Error fetching pages', error);
      toast.error('Błąd pobierania stron');
    } finally {
      setLoading(false);
    }
  };

  // Template version - increment when template changes to trigger auto-heal
  const TEMPLATE_VERSION = 4;

  // Generate HTML for landing page
  const generateLandingPageHtml = (page: LandingPage): string => {
    // Build storage URLs - paths are stored WITHOUT bucket prefix
    const cleanFilePath = page.file_path?.replace(/^file-uploads\//, '');
    const cleanOgPath = page.og_image_path?.replace(/^file-uploads\//, '');
    
    const ogImageUrl = cleanOgPath
      ? `${SUPABASE_URL}/storage/v1/object/public/file-uploads/${cleanOgPath}`
      : null;
    
    const title = page.landing_title || page.file_name || page.vanity_slug;
    const description = page.landing_description || `Pobierz: ${page.file_name || page.vanity_slug}`;
    const fileSize = page.file_size ? `${(page.file_size / 1024 / 1024).toFixed(2)} MB` : '';
    
    // Storage URL for OG sharing (this is what crawlers will see)
    const storageShareUrl = `${SUPABASE_URL}/storage/v1/object/public/landing-pages/${page.vanity_slug}.html`;
    // Download URL points to edge function for view count tracking
    const downloadUrl = `${SUPABASE_URL}/functions/v1/landing/${page.vanity_slug}/download`;

    // Include template version marker for auto-heal detection
    return `<!DOCTYPE html>
<!-- landing_template_v=${TEMPLATE_VERSION} -->
<html lang="pl">
<head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <meta name="description" content="${description}">
  
  <!-- Open Graph -->
  <meta property="og:type" content="website">
  <meta property="og:title" content="${title}">
  <meta property="og:description" content="${description}">
  <meta property="og:url" content="${storageShareUrl}">
  ${ogImageUrl ? `<meta property="og:image" content="${ogImageUrl}">
  <meta property="og:image:width" content="1200">
  <meta property="og:image:height" content="630">` : ''}
  
  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image">
  <meta name="twitter:title" content="${title}">
  <meta name="twitter:description" content="${description}">
  ${ogImageUrl ? `<meta name="twitter:image" content="${ogImageUrl}">` : ''}
  
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      color: #f8fafc;
    }
    .card {
      background: rgba(30, 41, 59, 0.95);
      border: 1px solid rgba(148, 163, 184, 0.15);
      border-radius: 24px;
      padding: 48px 40px;
      max-width: 480px;
      width: 100%;
      text-align: center;
      backdrop-filter: blur(20px);
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    }
    .preview {
      margin-bottom: 32px;
    }
    .preview img {
      max-height: 200px;
      max-width: 100%;
      object-fit: contain;
      border-radius: 12px;
      border: 1px solid rgba(148, 163, 184, 0.1);
    }
    .file-icon {
      width: 80px;
      height: 80px;
      margin: 0 auto 32px;
      background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 10px 30px -5px rgba(59, 130, 246, 0.4);
    }
    .file-icon svg {
      width: 40px;
      height: 40px;
      color: white;
    }
    h1 {
      font-size: 26px;
      font-weight: 700;
      margin-bottom: 12px;
      word-break: break-word;
      line-height: 1.3;
    }
    .description {
      color: #94a3b8;
      font-size: 16px;
      margin-bottom: 32px;
      line-height: 1.6;
    }
    .meta {
      color: #64748b;
      font-size: 14px;
      margin-bottom: 32px;
    }
    .download-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      padding: 18px 32px;
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: white;
      font-size: 17px;
      font-weight: 600;
      border: none;
      border-radius: 14px;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.2s ease;
      box-shadow: 0 8px 24px -4px rgba(59, 130, 246, 0.5);
    }
    .download-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px -4px rgba(59, 130, 246, 0.6);
    }
    .download-btn:focus {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }
    .download-btn svg {
      width: 22px;
      height: 22px;
    }
    .no-file {
      color: #64748b;
      font-style: italic;
      padding: 24px;
      background: rgba(100, 116, 139, 0.1);
      border-radius: 12px;
    }
    .footer {
      margin-top: 32px;
      font-size: 12px;
      color: #475569;
    }
    .footer a {
      color: #3b82f6;
      text-decoration: none;
    }
    .footer a:hover {
      text-decoration: underline;
    }
  </style>
</head>
<body>
  <div class="card">
    ${ogImageUrl 
      ? `<div class="preview"><img src="${ogImageUrl}" alt="${title}"></div>`
      : `<div class="file-icon">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        </div>`
    }
    <h1>${title}</h1>
    ${description ? `<p class="description">${description}</p>` : ''}
    ${fileSize || page.file_name ? `<p class="meta">${page.file_name || ''} ${fileSize ? `• ${fileSize}` : ''}</p>` : ''}
    ${cleanFilePath 
      ? `<a href="${downloadUrl}" class="download-btn" role="button" aria-label="Pobierz plik ${page.file_name || ''}">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Pobierz za darmo
        </a>`
      : `<p class="no-file">Plik będzie dostępny wkrótce</p>`
    }
    <p class="footer">Udostępnione przez <a href="https://lifactor.pl" target="_blank" rel="noopener">LIFactor</a></p>
  </div>
</body>
</html>`;
  };

  // Upload HTML to Storage with proper Content-Type using DELETE-then-UPLOAD pattern
  // This ensures Content-Type metadata is always correct (Supabase upsert can keep old metadata)
  const uploadLandingPageHtml = async (page: LandingPage): Promise<string | null> => {
    try {
      const html = generateLandingPageHtml(page);
      const fileName = `${page.vanity_slug}.html`;
      
      // Create blob with explicit UTF-8 encoding
      const encoder = new TextEncoder();
      const htmlBytes = encoder.encode(html);
      const htmlBlob = new Blob([htmlBytes], { type: 'text/html; charset=utf-8' });

      // STEP 1: Delete existing file first (ignore errors if file doesn't exist)
      logger.info(`Deleting existing HTML file (if any): ${fileName}`);
      await supabase.storage
        .from('landing-pages')
        .remove([fileName])
        .catch(() => {
          logger.info('No existing file to delete or delete failed (this is OK)');
        });

      // STEP 2: Upload fresh file with correct Content-Type
      logger.info('Uploading new HTML file with Content-Type: text/html; charset=utf-8');
      const { error: uploadError } = await supabase.storage
        .from('landing-pages')
        .upload(fileName, htmlBlob, { 
          upsert: false, // Don't use upsert - we just deleted the file
          contentType: 'text/html; charset=utf-8',
          cacheControl: 'no-cache'
        });

      if (uploadError) {
        logger.error('Error uploading landing page HTML', uploadError);
        return null;
      }

      const landingPageUrl = `${SUPABASE_URL}/storage/v1/object/public/landing-pages/${page.vanity_slug}.html`;
      logger.info(`HTML uploaded successfully: ${landingPageUrl}`);
      
      // Update DB with landing page URL
      await supabase
        .from('file_uploads')
        .update({ landing_page_url: landingPageUrl })
        .eq('id', page.id);

      return landingPageUrl;
    } catch (error) {
      logger.error('Error in uploadLandingPageHtml', error);
      return null;
    }
  };

  // STEP 1: Create new page with slug
  const handleCreatePage = async () => {
    if (!newSlug.trim()) {
      toast.error('Podaj URL strony');
      return;
    }

    // Validate slug format
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(newSlug.toLowerCase())) {
      toast.error('URL może zawierać tylko małe litery, cyfry i myślniki');
      return;
    }

    try {
      setCreating(true);

      // Check if slug exists
      const { data: existing } = await supabase
        .from('file_uploads')
        .select('id')
        .eq('vanity_slug', newSlug.toLowerCase())
        .single();

      if (existing) {
        toast.error('Ten URL już istnieje');
        return;
      }

      // Create new page record
      const { data: newPage, error } = await supabase
        .from('file_uploads')
        .insert({
          vanity_slug: newSlug.toLowerCase(),
          landing_title: newTitle || null,
          landing_description: newDescription || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Generate initial HTML (without file)
      await uploadLandingPageHtml(newPage);

      toast.success('Strona utworzona! Teraz dodaj plik i grafikę.');
      setNewSlug('');
      setNewTitle('');
      setNewDescription('');
      fetchPages();
    } catch (error) {
      logger.error('Error creating page', error);
      toast.error('Błąd tworzenia strony');
    } finally {
      setCreating(false);
    }
  };

  // STEP 2a: Upload file
  const handleFileUpload = async (pageId: string, file: File) => {
    try {
      setUploadingFileFor(pageId);

      const page = pages.find(p => p.id === pageId);
      if (!page) return;

      const filePath = `main/${page.vanity_slug}/${file.name}`;

      // Delete old file if exists
      if (page.file_path) {
        await supabase.storage.from('file-uploads').remove([page.file_path.replace('file-uploads/', '')]);
      }

      // Upload new file
      const { error: uploadError } = await supabase.storage
        .from('file-uploads')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Update DB - store path WITHOUT bucket prefix (bucket name is 'file-uploads')
      const { data: updatedPage, error: updateError } = await supabase
        .from('file_uploads')
        .update({
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          content_type: file.type,
        })
        .eq('id', pageId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Regenerate HTML
      await uploadLandingPageHtml(updatedPage);

      toast.success('Plik dodany!');
      fetchPages();
    } catch (error) {
      logger.error('Error uploading file', error);
      toast.error('Błąd przesyłania pliku');
    } finally {
      setUploadingFileFor(null);
    }
  };

  // STEP 2b: Upload OG image
  const handleImageUpload = async (pageId: string, file: File) => {
    try {
      setUploadingImageFor(pageId);

      const page = pages.find(p => p.id === pageId);
      if (!page) return;

      const imagePath = `og/${page.vanity_slug}-og.${file.name.split('.').pop()}`;

      // Delete old image if exists
      if (page.og_image_path) {
        await supabase.storage.from('file-uploads').remove([page.og_image_path.replace('file-uploads/', '')]);
      }

      // Upload new image
      const { error: uploadError } = await supabase.storage
        .from('file-uploads')
        .upload(imagePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Update DB - store path WITHOUT bucket prefix (bucket name is 'file-uploads')
      const { data: updatedPage, error: updateError } = await supabase
        .from('file_uploads')
        .update({
          og_image_path: imagePath,
        })
        .eq('id', pageId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Regenerate HTML with new OG image
      await uploadLandingPageHtml(updatedPage);

      toast.success('Grafika OG dodana!');
      fetchPages();
    } catch (error) {
      logger.error('Error uploading OG image', error);
      toast.error('Błąd przesyłania grafiki');
    } finally {
      setUploadingImageFor(null);
    }
  };

  // STEP 3: Save OG metadata (title/description)
  const handleSaveMetadata = async (pageId: string) => {
    try {
      setSavingMetadata(true);
      const page = pages.find(p => p.id === pageId);
      if (!page) return;

      const { data: updatedPage, error } = await supabase
        .from('file_uploads')
        .update({
          landing_title: editTitle || null,
          landing_description: editDescription || null,
        })
        .eq('id', pageId)
        .select()
        .single();

      if (error) throw error;

      // Regenerate HTML with new metadata
      await uploadLandingPageHtml(updatedPage);

      toast.success('Metadane zapisane!');
      setEditingPageId(null);
      fetchPages();
    } catch (error) {
      logger.error('Error saving metadata', error);
      toast.error('Błąd zapisywania metadanych');
    } finally {
      setSavingMetadata(false);
    }
  };

  // Start editing metadata
  const handleStartEditMetadata = (page: LandingPage) => {
    setEditingPageId(page.id);
    setEditTitle(page.landing_title || '');
    setEditDescription(page.landing_description || '');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingPageId(null);
    setEditTitle('');
    setEditDescription('');
  };

  // Regenerate HTML
  const handleRegenerate = async (pageId: string) => {
    try {
      setRegeneratingFor(pageId);
      const page = pages.find(p => p.id === pageId);
      if (!page) return;

      const url = await uploadLandingPageHtml(page);
      if (url) {
        toast.success('Strona wygenerowana!');
        fetchPages();
      } else {
        toast.error('Błąd generowania strony');
      }
    } finally {
      setRegeneratingFor(null);
    }
  };

  // Delete page
  const handleDelete = async (pageId: string) => {
    if (!confirm('Na pewno usunąć tę stronę?')) return;

    try {
      const page = pages.find(p => p.id === pageId);
      if (!page) return;

      // Delete files from storage
      const filesToDelete: string[] = [];
      if (page.file_path) {
        filesToDelete.push(page.file_path.replace('file-uploads/', ''));
      }
      if (page.og_image_path) {
        filesToDelete.push(page.og_image_path.replace('file-uploads/', ''));
      }
      
      if (filesToDelete.length > 0) {
        await supabase.storage.from('file-uploads').remove(filesToDelete);
      }

      // Delete HTML from landing-pages bucket
      await supabase.storage.from('landing-pages').remove([`${page.vanity_slug}.html`]);

      // Delete from DB
      const { error } = await supabase
        .from('file_uploads')
        .delete()
        .eq('id', pageId);

      if (error) throw error;

      toast.success('Strona usunięta');
      fetchPages();
    } catch (error) {
      logger.error('Error deleting page', error);
      toast.error('Błąd usuwania strony');
    }
  };

  // Get the PRIMARY share URL - Storage URL (serves HTML with correct Content-Type)
  // Edge Functions have sandbox CSP that forces text/plain, so we use Storage
  const getPrimaryShareUrl = (page: LandingPage): string => {
    return `${SUPABASE_URL}/storage/v1/object/public/landing-pages/${page.vanity_slug}.html`;
  };

  // Get the Lovable SPA URL (fallback for users)
  const getLovableUrl = (page: LandingPage): string => {
    return `${getBaseUrl()}/l/${page.vanity_slug}`;
  };

  // Get Edge Function URL (for debugging - has OG but Content-Type issues)
  const getEdgeFunctionUrl = (page: LandingPage): string => {
    return `${SUPABASE_URL}/functions/v1/landing/${page.vanity_slug}`;
  };

  // Copy Primary Share URL (Lovable link)
  const handleCopyPrimaryLink = async (page: LandingPage) => {
    const url = getPrimaryShareUrl(page);
    await navigator.clipboard.writeText(url);
    setCopiedId(page.id);
    toast.success('Link skopiowany!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Generate LinkedIn Post text (uses Edge Function URL for OG support)
  const generateLinkedInPost = (page: LandingPage): string => {
    const title = page.landing_title || page.file_name || page.vanity_slug;
    const description = page.landing_description || '';
    const url = getEdgeFunctionUrl(page); // Edge Function URL for OG crawlers
    
    // Take first 1-2 sentences from description
    const shortDesc = description.split('.').slice(0, 2).join('.').trim();
    
    let post = `🎯 ${title}\n\n`;
    if (shortDesc) {
      post += `${shortDesc}.\n\n`;
    }
    post += `👉 Pobierz bezpłatnie: ${url}\n\n`;
    post += `#marketing #socialselling #linkedin #bezplatnie`;
    
    return post;
  };

  // Copy LinkedIn Post to clipboard
  const handleCopyLinkedInPost = async (page: LandingPage) => {
    const postText = generateLinkedInPost(page);
    await navigator.clipboard.writeText(postText);
    setCopiedId(`${page.id}-linkedin`);
    toast.success('Post skopiowany! Wklej go na LinkedIn i dodaj miniaturę ręcznie.');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Download OG image as thumbnail
  const handleDownloadThumbnail = async (page: LandingPage) => {
    if (!page.og_image_path) {
      toast.error('Brak grafiki OG do pobrania');
      return;
    }
    
    try {
      setDownloadingThumbnailFor(page.id);
      const cleanPath = page.og_image_path.replace(/^file-uploads\//, '');
      const imageUrl = `${SUPABASE_URL}/storage/v1/object/public/file-uploads/${cleanPath}`;
      
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      
      const blob = await response.blob();
      const extension = page.og_image_path.split('.').pop() || 'png';
      const fileName = `${page.vanity_slug}-linkedin.${extension}`;
      
      // Create download link
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      
      toast.success('Miniatura pobrana! Dodaj ją ręcznie do posta na LinkedIn.');
    } catch (error) {
      logger.error('Error downloading thumbnail', error);
      toast.error('Błąd pobierania miniatury');
    } finally {
      setDownloadingThumbnailFor(null);
    }
  };

  // Format file size
  const formatSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-6">

      {/* Info Card */}
      <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
        <Check className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700 dark:text-blue-300">
          <strong>3 kroki:</strong> 1) Utwórz stronę z URL → 2) Dodaj plik i grafikę OG → 3) Ustaw metadane tytułu/opisu.
          Po każdej zmianie strona HTML jest automatycznie regenerowana.
        </AlertDescription>
      </Alert>

      {/* Create New Page */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Krok 1: Utwórz nową stronę
          </CardTitle>
          <CardDescription>
            Najpierw podaj URL strony, potem dodasz plik i grafikę.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="slug">Custom URL *</Label>
              <Input
                id="slug"
                placeholder="np. ebook-marketing"
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tylko małe litery, cyfry, myślniki
              </p>
            </div>
            <div>
              <Label htmlFor="title">Tytuł strony</Label>
              <Input
                id="title"
                placeholder="np. Darmowy E-book Marketing"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="desc">Opis</Label>
              <Input
                id="desc"
                placeholder="np. Pobierz za darmo!"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleCreatePage} disabled={creating || !newSlug.trim()}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Tworzę...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Utwórz stronę
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Pages List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Krok 2 & 3: Zarządzaj stronami ({pages.length})
          </CardTitle>
          <CardDescription>
            Dodaj plik, grafikę OG i ustaw metadane dla każdej strony.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : pages.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Brak stron. Utwórz pierwszą powyżej.
            </p>
          ) : (
            <div className="space-y-4">
              {pages.map((page) => (
                <div
                  key={page.id}
                  className="border rounded-lg p-4 space-y-4"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm font-medium">
                          /{page.vanity_slug}
                        </span>
                      </div>
                      {page.landing_title && (
                        <p className="text-sm font-medium mt-1">{page.landing_title}</p>
                      )}
                      {page.landing_description && (
                        <p className="text-xs text-muted-foreground">{page.landing_description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Delete */}
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(page.id)}
                        title="Usuń stronę"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* File & Image Upload */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* File */}
                    <div className="border rounded-lg p-3 bg-muted/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Plik do pobrania
                        </span>
                        <input
                          type="file"
                          ref={activePageId === page.id ? fileInputRef : undefined}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(page.id, file);
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActivePageId(page.id);
                            setTimeout(() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0];
                                if (file) handleFileUpload(page.id, file);
                              };
                              input.click();
                            }, 0);
                          }}
                          disabled={uploadingFileFor === page.id}
                        >
                          {uploadingFileFor === page.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {page.file_name ? (
                        <div className="text-xs">
                          <p className="font-medium truncate">{page.file_name}</p>
                          <p className="text-muted-foreground">{formatSize(page.file_size)}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Brak pliku</p>
                      )}
                    </div>

                    {/* OG Image */}
                    <div className="border rounded-lg p-3 bg-muted/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium flex items-center gap-2">
                          <Image className="h-4 w-4" />
                          Grafika OG
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleImageUpload(page.id, file);
                            };
                            input.click();
                          }}
                          disabled={uploadingImageFor === page.id}
                        >
                          {uploadingImageFor === page.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {page.og_image_path ? (
                        <div className="space-y-2">
                          <img
                            src={getOgImageUrl(page.og_image_path) || ''}
                            alt="OG Preview"
                            className="w-full h-20 object-cover rounded"
                          />
                          {/* Show OG Image URL for copy */}
                          <div className="flex items-center gap-1">
                            <Input
                              readOnly
                              value={getOgImageUrl(page.og_image_path) || ''}
                              className="text-[10px] font-mono h-6 bg-muted/50"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 flex-shrink-0"
                              onClick={() => {
                                navigator.clipboard.writeText(getOgImageUrl(page.og_image_path) || '');
                                toast.success('URL grafiki skopiowany!');
                              }}
                              title="Kopiuj URL grafiki OG"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Brak grafiki</p>
                      )}
                    </div>
                  </div>

                  {/* Step 3: OG Metadata Form */}
                  <div className="border rounded-lg p-3 bg-blue-50/50 dark:bg-blue-950/20 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        Krok 3: Metadane OG
                      </span>
                      {editingPageId !== page.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStartEditMetadata(page)}
                        >
                          Edytuj
                        </Button>
                      )}
                    </div>
                    
                    {editingPageId === page.id ? (
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor={`edit-title-${page.id}`} className="text-xs">Tytuł OG</Label>
                          <Input
                            id={`edit-title-${page.id}`}
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            placeholder="Tytuł wyświetlany na LinkedIn/Facebook"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor={`edit-desc-${page.id}`} className="text-xs">Opis OG</Label>
                          <Input
                            id={`edit-desc-${page.id}`}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            placeholder="Krótki opis wyświetlany pod tytułem"
                            className="mt-1"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveMetadata(page.id)}
                            disabled={savingMetadata}
                          >
                            {savingMetadata ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : null}
                            Zapisz
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelEdit}
                          >
                            Anuluj
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs space-y-1">
                        <p><strong>Tytuł:</strong> {page.landing_title || <span className="text-muted-foreground italic">brak</span>}</p>
                        <p><strong>Opis:</strong> {page.landing_description || <span className="text-muted-foreground italic">brak</span>}</p>
                      </div>
                    )}
                  </div>

                  {/* Links Section */}
                  <div className="space-y-3 pt-2 border-t">
                    {/* Info Note */}
                    <div className="flex items-start gap-2 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-xs text-blue-700 dark:text-blue-300">
                      <span className="flex-shrink-0">ℹ️</span>
                      <span>
                        <strong>Link Edge Function</strong> = do udostępniania na LinkedIn/FB (crawlery dostają OG, użytkownicy redirect do SPA). 
                        <strong> Link SPA</strong> = bezpośrednio do aplikacji.
                      </span>
                    </div>

                    {/* Edge Function URL (Primary for sharing) */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1 flex items-center gap-1">
                          🔗 Link Edge Function (do udostępniania)
                        </p>
                        <div className="text-xs font-mono bg-green-50 dark:bg-green-950/30 p-2 rounded border border-green-200 dark:border-green-800 truncate">
                          {getEdgeFunctionUrl(page)}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(getEdgeFunctionUrl(page));
                            setCopiedId(`${page.id}-edge-main`);
                            toast.success('Link Edge Function skopiowany!');
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          title="Kopiuj link Edge Function"
                        >
                          {copiedId === `${page.id}-edge-main` ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(getEdgeFunctionUrl(page), '_blank')}
                          title="Otwórz (redirect do SPA)"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* SPA URL */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-1 flex items-center gap-1">
                          🌐 Link SPA (użytkownicy)
                        </p>
                        <div className="text-xs font-mono bg-purple-50 dark:bg-purple-950/30 p-2 rounded border border-purple-200 dark:border-purple-800 truncate">
                          {getLovableUrl(page)}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(getLovableUrl(page));
                            setCopiedId(`${page.id}-spa`);
                            toast.success('Link SPA skopiowany!');
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          title="Kopiuj link SPA"
                        >
                          {copiedId === `${page.id}-spa` ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(getLovableUrl(page), '_blank')}
                          title="Otwórz SPA"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* LinkedIn Posting Helpers */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCopyLinkedInPost(page)}
                        className="flex items-center gap-2"
                        aria-label="Kopiuj post na LinkedIn"
                      >
                        {copiedId === `${page.id}-linkedin` ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                        Kopiuj post LinkedIn
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDownloadThumbnail(page)}
                        disabled={!page.og_image_path || downloadingThumbnailFor === page.id}
                        className="flex items-center gap-2"
                        title={!page.og_image_path ? 'Najpierw dodaj grafikę OG' : 'Pobierz miniaturę do LinkedIn'}
                        aria-label="Pobierz miniaturę do LinkedIn"
                      >
                        {downloadingThumbnailFor === page.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        Pobierz miniaturę
                      </Button>
                    </div>
                    
                    {/* Advanced section */}
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="advanced" className="border-0">
                        <AccordionTrigger className="text-xs text-muted-foreground hover:no-underline py-2">
                          <span className="flex items-center gap-1">
                            Zaawansowane
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3">
                            {/* Storage URL - for debugging */}
                            <div className="flex items-center gap-1">
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-medium text-muted-foreground mb-1 flex items-center gap-1">
                                  📦 Storage URL (statyczny HTML)
                                </p>
                                <div className="text-[10px] font-mono bg-muted/30 p-1.5 rounded truncate">
                                  {getPrimaryShareUrl(page)}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(getPrimaryShareUrl(page));
                                  setCopiedId(`${page.id}-storage`);
                                  toast.info('Storage URL skopiowany');
                                  setTimeout(() => setCopiedId(null), 2000);
                                }}
                                title="Kopiuj Storage URL"
                                className="h-7 w-7 p-0"
                              >
                                {copiedId === `${page.id}-storage` ? (
                                  <Check className="h-3 w-3 text-green-500" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )}
                              </Button>
                            </div>

                            {/* Regenerate Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRegenerate(page.id)}
                              disabled={regeneratingFor === page.id}
                              className="w-full"
                            >
                              {regeneratingFor === page.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Regeneruję...
                                </>
                              ) : (
                                'Regeneruj HTML'
                              )}
                            </Button>

                            {/* Test OG on LinkedIn */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(getEdgeFunctionUrl(page))}`, '_blank')}
                              className="w-full text-xs"
                            >
                              <ExternalLink className="h-3 w-3 mr-2" />
                              Testuj OG na LinkedIn
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hidden file inputs */}
      <input type="file" ref={fileInputRef} className="hidden" />
      <input type="file" ref={imageInputRef} accept="image/*" className="hidden" />
    </div>
  );
};
